import { Fragment, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { WhatHappensNextDisclosure, WhatHappensNextPanel } from "@/components/WhatHappensNextPanel";
import { useNavigate, useLocation } from "react-router-dom";
import { useUrlState } from "@/hooks/useUrlState";
import { useIncludeTest, withIncludeTest } from "@/hooks/useIncludeTest";
import { IncludeTestToggle } from "@/components/IncludeTestToggle";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { TestBadge, IntentBadge, EmptyState, ErrorState } from "@/components/common";
import {
  COLUMN_WINDOW, TABLE_WINDOW, ACTIVE_PAGE_SIZE, UNPLACED_COLUMN,
  formatCount, columnCountLabel, nextFollowUpAt, windowed, unplacedRows,
} from "@/lib/pipelineBoard";
import { KanbanSquare, Settings2, LayoutGrid, TableProperties, Lock } from "lucide-react";
import { CallLogDialog } from "@/pages/CallsPage";
import { ConsultationActions } from "@/components/ConsultationActions";
import { AdmissionOutcomeActions } from "@/components/AdmissionOutcomeActions";
import { WorkflowSetupRequiredNotice, isWorkflowSetupBlock } from "@/components/WorkflowSetupRequired";
import { WorkflowOutcomeDialog, outcomeRequiredTarget } from "@/components/WorkflowOutcomeDialog";
import { StageGateExplanation } from "@/components/StageGateExplanation";
import { gateView, clientGateView } from "@/lib/stageGate";

const STAGE_COLORS = { slate: "border-t-slate-400", sky: "border-t-sky-500", amber: "border-t-amber-500", green: "border-t-emerald-500", violet: "border-t-violet-500", red: "border-t-red-500" };

// 2026-09 R6 — "Include converted customers". A converted (lifecycle completed)
// opportunity is read-only on this board: its sales pursuit is closed and
// its onward progress is its Case's own stage, moved from the Case page.
// The server decides what "converted" means: every row from
// GET /contacts/grid/converted carries `is_converted: true`.
export const isCompletedRow = (r) => r?.is_converted === true || r?.lifecycle_status === "completed";
// Converted customers are fetched one page at a time (exact total from the
// server, no silent cap); `cp` in the URL records how many pages are loaded so
// a refresh / Back restores the same view.
export const CONVERTED_PAGE_SIZE = 100;
export const COMPLETED_READ_ONLY_MSG =
  "Converted customer — read-only on the board. Open the customer to follow its Case.";

// Board placement: every row goes to the column named by its own canonical
// position (row.stage_name — the Case's current stage once one exists). A
// COMPLETED row whose position isn't a column on this board (legacy/unmapped
// name) falls back to the lifecycle's terminal stage — the registry's
// `terminal` flag (Closure), never a hardcoded name — so a completed record
// is never silently dropped. Each row is placed exactly once.
export function placeRowsOnBoard(stages, rows) {
  const cols = (stages || []).filter((s) => s.active !== false);
  const map = {};
  cols.forEach((s) => { map[s.name] = []; });
  const terminal = cols.find((s) => s.terminal)
    || cols.slice().sort((a, b) => a.order - b.order)[cols.length - 1];
  const seen = new Set();
  (rows || []).forEach((r) => {
    if (seen.has(r.opportunity_id)) return;
    seen.add(r.opportunity_id);
    let key = r.stage_name;
    if (isCompletedRow(r) && !(key in map) && terminal) key = terminal.name;
    (map[key] = map[key] || []).push(r);
  });
  return map;
}

// The ACTIVE board is paged (with_meta=true): the server reports the exact
// total (bounded, "5,000+" past it) and whether more pages exist, so a board
// bigger than one page says so instead of silently stopping.
export const activeGridPath = (product, offset = 0, limit = ACTIVE_PAGE_SIZE, includeTest = false) =>
  withIncludeTest(`/contacts/grid?product_code=${product}&lifecycle=active&unified=true&with_meta=true&offset=${offset}&limit=${limit}`, includeTest);
const EMPTY_ACTIVE = { items: null, total: 0, capped: false, hasMore: false, loading: false, error: null };
// A pre-paging server answers a bare list; treat it as one complete page.
const asActivePage = (data) => (Array.isArray(data)
  ? { items: data, total: data.length, total_capped: false, has_more: false } : data);

const ageDays = (d) => d ? Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 86400000)) : "—";

const OverrideDialog = ({ pending, setPending, onConfirm }) => {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Stage gate — mandatory tasks incomplete</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">This stage still has open mandatory tasks. Provide a reason to override (audited), or cancel and complete the tasks first.</p>
          <Textarea data-testid="stage-override-reason" rows={3} placeholder="Override reason (required)…" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <DialogFooter>
          <Button data-testid="stage-override-confirm" variant="destructive" disabled={reason.trim().length < 5}
            onClick={() => { onConfirm(reason); setReason(""); }}>Override & move</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// 2026-09 R4 — the builder edits SAFE display metadata only (colour,
// description). Stage structure — name, order, category, archive/activate,
// adding a stage — comes from the canonical lifecycle registry and changes
// only through a new registry version + the admin-triggered reconciliation;
// the backend refuses structural edits (409 structural_stage_edit_forbidden),
// so they are no longer offered here at all.
const PipelineBuilder = ({ open, setOpen, onChanged }) => {
  const [pipelines, setPipelines] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/pipelines").then((r) => { setPipelines(r.data); if (!sel && r.data[0]) setSel(r.data[0].pipeline_id); });
  }, [sel]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const pipe = pipelines?.find((p) => p.pipeline_id === sel);

  const patchStage = async (stage_id, updates) => {
    try { await api.patch(`/admin/stages/${stage_id}`, updates); load(); onChanged(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const stages = (pipe?.stages || []).slice().sort((a, b) =>
    (a.active === false) - (b.active === false) || a.order - b.order);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Pipeline builder</DialogTitle></DialogHeader>
        {!pipelines ? <Skeleton className="h-40" /> : (
          <div className="space-y-3">
            <Select value={sel || ""} onValueChange={setSel}>
              <SelectTrigger data-testid="builder-pipeline-select" className="h-9"><SelectValue placeholder="Pick pipeline" /></SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => <SelectItem key={p.pipeline_id} value={p.pipeline_id}>{p.product_code}</SelectItem>)}
              </SelectContent>
            </Select>
            {pipe && (
              <div className="space-y-2">
                <div className="rounded-md border bg-muted/40 p-2 text-[11px] text-muted-foreground" data-testid="builder-structure-note">
                  {pipe.lifecycle ? (
                    <>Stage structure is the canonical <b>{pipe.lifecycle.label}</b> lifecycle
                      (registry v{pipe.lifecycle.version}){pipe.lifecycle.reconciled ? "" : " — this database's stored stages are not reconciled onto it yet (an administrator-run lifecycle reconciliation step)"}.
                      Names, order and archiving can only change through a new registry version.</>
                  ) : (
                    <>This product has no canonical lifecycle yet; its stage structure can only change through a new registry version.</>
                  )}{" "}Only colour and description are editable here.
                </div>
                {stages.map((s) => (
                  <div key={s.stage_id} data-testid={`builder-stage-${s.stage_id}`}
                    className={`flex items-center gap-2 p-2 rounded-md border ${s.active === false ? "opacity-50" : ""}`}>
                    <span className="text-sm flex-1 min-w-0 truncate" data-testid={`builder-stage-name-${s.stage_id}`}>
                      {s.name}
                      {s.active === false && <Badge variant="outline" className="ml-2 text-[9px]">Archived</Badge>}
                    </span>
                    <Input className="h-8 text-xs w-56" defaultValue={s.description || ""} placeholder="Description"
                      data-testid={`builder-stage-description-${s.stage_id}`}
                      onBlur={(e) => e.target.value !== (s.description || "") && patchStage(s.stage_id, { description: e.target.value })} />
                    <Select value={s.color || "slate"} onValueChange={(c) => patchStage(s.stage_id, { color: c })}>
                      <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(STAGE_COLORS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function PipelinesPage() {
  const { hasCap } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = encodeURIComponent(location.pathname + location.search);
  // 2026-09 P4 Round 2 (Problem 10) — product/view now live in the URL
  // (useUrlState), not localStorage: a localStorage-only preference
  // survives a page reload but not a Back navigation to a specific prior
  // pipeline/view (the whole point of this page's own navigation
  // contract) — the URL is what Back actually restores. Falls back to
  // localStorage's last value only when the URL doesn't already say
  // otherwise, so an existing bookmark/habit isn't broken.
  const [plUrlState, setPlUrlState] = useUrlState({
    product: localStorage.getItem("pl-product") || "", view: localStorage.getItem("pl-view") || "kanban",
    completed: false, cp: 1,
  });
  const { product, view, completed: includeCompleted, cp: convertedPages } = plUrlState;
  // Round 7 Workstream F - TEST cards are hidden unless an authorised admin
  // turns the visible toggle on (server-enforced; see backend/test_visibility.py).
  const { includeTest, canInclude, setIncludeTest } = useIncludeTest();
  const setProduct = (v) => { localStorage.setItem("pl-product", v); setPlUrlState({ product: v }); };
  const setView = (v) => { localStorage.setItem("pl-view", v); setPlUrlState({ view: v }); };
  const [products, setProducts] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [active, setActive] = useState(EMPTY_ACTIVE);   // ACTIVE opportunities (the board's default answer), paged
  const rows = active.items;
  const activeToken = useRef(0);             // discards a response for a product already left
  const activeLoaded = useRef(0);            // rows loaded so far - a reload restores as many
  const lastProduct = useRef(null);
  const [pipelineError, setPipelineError] = useState(false);
  const [colShown, setColShown] = useState({});   // Kanban: cards rendered per column
  const [tableShown, setTableShown] = useState(TABLE_WINDOW);
  // Converted customers, only fetched while the toggle is on.
  const [converted, setConverted] = useState({ items: [], total: 0, loading: false, error: false });
  const convToken = useRef(0);               // discards a response for a product/toggle already left
  const convPagesRef = useRef(1);
  convPagesRef.current = Math.max(1, Number(convertedPages) || 1);
  const [drag, setDrag] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  // R13 Workstream 7: the persistent explanation of a refused move (table AND Kanban).
  const [gateExplain, setGateExplain] = useState(null);
  const [journeyRows, setJourneyRows] = useState(() => new Set()); // table rows whose "What happens next" is expanded
  const toggleJourney = (id) => setJourneyRows((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const [gateAction, setGateAction] = useState(null); // {label, run}: the remedy offered from the explanation
  const [builderOpen, setBuilderOpen] = useState(false);
  const [callBlocker, setCallBlocker] = useState(null); // {opp, stage}
  const [consultBlocker, setConsultBlocker] = useState(null); // {opp, stage, appt}
  const [admissionBlocker, setAdmissionBlocker] = useState(null); // {opp, stage}
  const [backwardPrompt, setBackwardPrompt] = useState(null); // {opp, stage}
  const [backwardReason, setBackwardReason] = useState("");
  const [setupBlock, setSetupBlock] = useState(null); // {opp, stage, detail} — R4 workflow_setup_required
  const [outcomeFor, setOutcomeFor] = useState(null); // {caseId, taskId, contactId} — governed Case outcome_required

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      if (!product && r.data[0]) setProduct(r.data[0].code);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadActive = useCallback(async () => {
    const token = ++activeToken.current;
    setActive((a) => ({ ...a, loading: true, error: null }));
    try {
      let items = []; let meta = null;
      // Restore as many rows as were loaded before (a reload after a stage
      // move must not shrink a board the user had paged further into).
      do {
        // eslint-disable-next-line no-await-in-loop
        const r = await api.get(activeGridPath(product, items.length, ACTIVE_PAGE_SIZE, includeTest));
        if (token !== activeToken.current) return;
        meta = asActivePage(r.data);
        items = [...items, ...meta.items];
      } while (meta.has_more && items.length < activeLoaded.current);
      activeLoaded.current = items.length;
      setActive({ items, total: meta.total, capped: !!meta.total_capped, hasMore: !!meta.has_more,
                  loading: false, error: null });
    } catch (e) {
      if (token === activeToken.current) {
        setActive((a) => ({ ...a, loading: false, error: errDetail(e, "Couldn't load opportunities") }));
      }
    }
  }, [product, includeTest]);

  const load = useCallback(() => {
    if (!product) return;
    if (lastProduct.current !== product) {   // a different pipeline: start clean
      lastProduct.current = product;
      activeLoaded.current = 0;
      setActive(EMPTY_ACTIVE); setPipeline(null); setColShown({}); setTableShown(TABLE_WINDOW);
    }
    // unified=true (Unified Pipeline, P1): the stage list includes this
    // product's Process/Case stages after Process / Handover, and each
    // row's stage_name reflects the linked Case's own current_stage once
    // an opportunity has crossed into Process — one continuous board
    // instead of stopping at the handoff.
    setPipelineError(false);
    api.get(`/pipelines/${product}?unified=true`).then((r) => setPipeline(r.data)).catch(() => setPipelineError(true));
    // Default: active-only (paged). Converted customers are a SEPARATE, paged
    // request that only runs while the toggle is on - the default board's cost
    // is unchanged.
    loadActive();
    const token = ++convToken.current;
    if (!includeCompleted) { setConverted({ items: [], total: 0, loading: false, error: false }); return; }
    // Restore every page the URL says was loaded (a refresh keeps the view).
    setConverted((c) => ({ ...c, loading: true, error: false }));
    (async () => {
      let items = []; let total = 0;
      try {
        for (let page = 0; page < convPagesRef.current; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const r = await api.get(withIncludeTest(`/contacts/grid/converted?product_code=${product}&unified=true`
            + `&offset=${items.length}&limit=${CONVERTED_PAGE_SIZE}`, includeTest));
          if (token !== convToken.current) return;
          items = [...items, ...r.data.items]; total = r.data.total;
          if (!r.data.has_more) break;
        }
        setConverted({ items, total, loading: false, error: false });
      } catch (e) {
        if (token === convToken.current) setConverted({ items, total, loading: false, error: true });
      }
    })();
  }, [product, includeCompleted, includeTest, loadActive]);
  useEffect(() => { load(); }, [load]);

  const loadMoreActive = async () => {
    if (active.loading || !active.hasMore) return;
    const token = activeToken.current;
    setActive((a) => ({ ...a, loading: true, error: null }));
    try {
      const r = await api.get(activeGridPath(product, active.items.length, ACTIVE_PAGE_SIZE, includeTest));
      if (token !== activeToken.current) return;
      const meta = asActivePage(r.data);
      const have = new Set(active.items.map((x) => x.opportunity_id));
      const items = [...active.items, ...meta.items.filter((x) => !have.has(x.opportunity_id))];
      activeLoaded.current = items.length;
      setActive({ items, total: meta.total, capped: !!meta.total_capped, hasMore: !!meta.has_more,
                  loading: false, error: null });
    } catch (e) {
      if (token === activeToken.current) {
        setActive((a) => ({ ...a, loading: false, error: errDetail(e, "Couldn't load more opportunities") }));
      }
    }
  };

  // Active rows first; a converted row is never added twice (an opportunity
  // is one lifecycle_status, so the two sets are disjoint - the guard is defence).
  const boardRows = useMemo(() => {
    if (!rows) return rows;
    const seen = new Set(rows.map((r) => r.opportunity_id));
    return [...rows, ...converted.items.filter((r) => !seen.has(r.opportunity_id))];
  }, [rows, converted.items]);
  const byStage = useMemo(() => placeRowsOnBoard(pipeline?.stages, boardRows), [pipeline, boardRows]);
  const convertedHasMore = converted.items.length < converted.total;
  // Kanban columns: the registry's active stages in order, plus a catch-all for
  // any row whose stage isn't one of them, so every row the Table lists is also
  // a card (same rows, same counts, on both views).
  const kanbanColumns = useMemo(() => {
    const cols = (pipeline?.stages || []).filter((s) => s.active !== false)
      .sort((a, b) => a.order - b.order)
      .map((s) => ({ key: s.stage_id, name: s.name, stage: s, rows: byStage[s.name] || [] }));
    const stray = unplacedRows(pipeline?.stages, byStage);
    if (stray.length) cols.push({ key: "__unplaced__", name: UNPLACED_COLUMN, stage: null, rows: stray });
    return cols;
  }, [pipeline, byStage]);
  const tableWindow = useMemo(() => windowed(boardRows || [], tableShown), [boardRows, tableShown]);

  const loadMoreConverted = async () => {
    if (converted.loading || !convertedHasMore) return;
    const token = convToken.current;
    setConverted((c) => ({ ...c, loading: true, error: false }));
    try {
      const r = await api.get(withIncludeTest(`/contacts/grid/converted?product_code=${product}&unified=true`
        + `&offset=${converted.items.length}&limit=${CONVERTED_PAGE_SIZE}`, includeTest));
      if (token !== convToken.current) return;
      const have = new Set(converted.items.map((x) => x.opportunity_id));
      const items = [...converted.items, ...r.data.items.filter((x) => !have.has(x.opportunity_id))];
      setConverted({ items, total: r.data.total, loading: false, error: false });
      setPlUrlState({ cp: Math.ceil(items.length / CONVERTED_PAGE_SIZE) });
    } catch (e) {
      if (token === convToken.current) setConverted((c) => ({ ...c, loading: false, error: true }));
    }
  };

  const moveStage = async (opp, stage, override = false, reason = null) => {
    if (isCompletedRow(opp)) {
      // Never offered by the UI (completed cards aren't draggable and their
      // table stage picker is disabled) — and the server refuses it anyway
      // (pipeline.transition_stage: opportunity_completed).
      toast.error(COMPLETED_READ_ONLY_MSG);
      return;
    }
    try {
      // 2026-09 R4 canonical board: `case_stage` marks a stage the linked
      // Case owns (incl. Partnership's shared "Agreed to Proceed"/"Signup");
      // `board_activation` marks the canonical Process entry a no-Case card
      // can be dropped on to activate its Case (the old "Process / Handover"
      // drop). A non-lifecycle product's board carries neither flag and
      // keeps the legacy dispatch below unchanged.
      const lifecycleBoard = !!stage.lifecycle_key;
      if ((stage.case_stage || stage.category === "process") && opp.case_id) {
        // The Case's own transition service (registry gates, reversal rules,
        // the no-bypass "Workflow setup required" block) — never a raw
        // opportunity stage write.
        await api.post(`/cases/${opp.case_id}/stage`, { stage: stage.name, override, reason });
      } else if (stage.category === "process") {
        if (lifecycleBoard ? stage.board_activation : !stage.synthetic) {
          // The real "Process / Handover" boundary stage itself — this is
          // the actual Signup->Process gate: activates/attaches the Case
          // (idempotent) and advances the opportunity's own stage in one
          // call, gated exactly like the approval-driven handoff.
          await api.post("/cases", { contact_id: opp.contact_id, opportunity_id: opp.opportunity_id,
                                     product_code: opp.product_code, override, reason });
        } else {
          const entry = (pipeline?.stages || []).find((s) => s.board_activation)?.name || "Process / Handover";
          setGateExplain(clientGateView({
            subject: opp.contact_name, current: opp.stage_name, requested: stage.name,
            message: `${opp.contact_name} hasn't entered Process yet.`,
            nextAction: `Move to "${entry}" first - that is where the Case is activated.`}));
          return;
        }
      } else if (stage.synthetic) {
        setGateExplain(clientGateView({
          subject: opp.contact_name, current: opp.stage_name, requested: stage.name,
          message: `"${stage.name}" isn't set up on this pipeline yet.`,
          nextAction: "An administrator must reconcile the canonical lifecycle first.", retryAllowed: false}));
        return;
      } else {
        await api.post(`/opportunities/${opp.opportunity_id}/stage`,
          { stage_id: stage.stage_id, override, reason });
      }
      toast.success(`${opp.contact_name} → ${stage.name}`);
      setPendingMove(null); setCallBlocker(null); setConsultBlocker(null); setAdmissionBlocker(null);
      setBackwardPrompt(null); setBackwardReason("");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const blockerType = d && typeof d === "object" ? d.blocker_type : null;
      const gctx = { requested: stage.name, current: opp.stage_name, subject: opp.contact_name };
      const outcomeTarget = outcomeRequiredTarget(e, opp);
      if (outcomeTarget) {
        // Registry-governed Case: a forward drag needs its outcome — open the
        // ONE pending task's form right here (same contract as the Case stage
        // rail). The card stays where it is until the outcome is recorded.
        setPendingMove(null);
        // The refusal is explained first (which outcome, which rule); the form is one click away.
        setGateExplain(gateView(e, gctx));
        setGateAction({ label: "Open the outcome form", run: () => setOutcomeFor(outcomeTarget) });
      } else if (e?.response?.status === 409 && isWorkflowSetupBlock(d)) {
        // 2026-09 R4 — the Case isn't mapped onto the canonical workflow:
        // its own distinct, non-overridable notice (admin-only way forward),
        // never a generic toast and never the Override dialog.
        setPendingMove(null);
        setSetupBlock({ opp, stage, detail: d });
      } else if (e?.response?.status === 409 && blockerType === "backward_reason_required") {
        // Authorized backward correction — needs a real reason, never the
        // generic Override dialog (this isn't overriding anything).
        setBackwardPrompt({ opp, stage }); setBackwardReason("");
      } else if (e?.response?.status === 409 && blockerType === "required_call_outcome") {
        // Route to the actual required action (the same Call Outcome
        // dialog Calls/Customer 360 already use) instead of a generic
        // "mandatory tasks" override prompt.
        setCallBlocker({ opp, stage });
      } else if (e?.response?.status === 409 && blockerType === "required_consultation_outcome") {
        const apptId = d.context?.appointment_id;
        if (apptId) {
          setGateExplain(gateView(e, gctx));
          setGateAction({ label: "Record the consultation outcome", run: () =>
            api.get(`/appointments/${apptId}`).then((r) => setConsultBlocker({ opp, stage, appt: r.data }))
              .catch(() => toast.error("Could not open the consultation outcome form.")) });
        } else {
          setGateExplain(gateView(e, gctx));
        }
      } else if (e?.response?.status === 409 && blockerType === "required_admission_outcome") {
        // Same canonical Admission Outcome dialog Customer 360 uses —
        // this used to be one of the "no specific UI yet" blocker types
        // below; it now has one.
        setAdmissionBlocker({ opp, stage });
      } else if (e?.response?.status === 409 && blockerType && !["required_task", "other"].includes(blockerType)) {
        // A blocker type with no specific UI yet (required_payment, required_approval,
        // required_document, ...) - never silently offer a generic override for these; explain
        // it in a persistent dialog (not a toast that vanishes).
        setGateExplain(gateView(e, gctx));
      } else if (e?.response?.status === 409 && d?.overridable === false) {
        // `overridable` is the backend's own single source of truth
        // (pipeline.py) — a "required_task"-labelled blocker can still be
        // genuinely hard-blocked (a mixed set that includes an evidence-
        // backed prerequisite), and must never get the generic Override
        // dialog just because its blocker_type looks generic.
        setGateExplain(gateView(e, gctx));
      } else if (e?.response?.status === 409 && hasCap("stage.override")) {
        setPendingMove({ opp, stage });
      } else if (e?.response?.status === 409) {
        setGateExplain(gateView(e, gctx));
      } else { setGateExplain(gateView(e, gctx)); }
    }
  };

  const onDrop = (stageName) => {
    if (!drag) return;
    if (isCompletedRow(drag)) { toast.error(COMPLETED_READ_ONLY_MSG); setDrag(null); return; }
    const stage = pipeline.stages.find((s) => s.name === stageName);
    if (stage && drag.stage_name !== stageName) moveStage(drag, stage);
    setDrag(null);
  };

  const renderKanbanCard = (r) => {
    const done = isCompletedRow(r);
    return (
      <Card key={r.opportunity_id} draggable={!done}
        onDragStart={done ? (e) => e.preventDefault() : () => setDrag(r)}
        data-testid={`kanban-card-${r.opportunity_id}`}
        data-completed={done ? "true" : undefined}
        aria-readonly={done || undefined}
        title={done ? COMPLETED_READ_ONLY_MSG : undefined}
        className={done ? "cursor-default border-dashed bg-muted/40" : "cursor-grab active:cursor-grabbing"}>
        <CardContent className="p-2.5 space-y-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <button className="text-sm font-medium truncate hover:underline text-left min-w-0"
              data-testid={`kanban-card-open-${r.opportunity_id}`}
              onClick={() => navigate(`/customers/${r.contact_id}?return_to=${returnTo}`)}>{r.contact_name}</button>
            <TestBadge show={r.is_test_record} />
            {done && (
              <Badge variant="outline" data-testid={`kanban-card-completed-${r.opportunity_id}`}
                className="ml-auto shrink-0 text-[9px] gap-1 border-[hsl(var(--success))]/40 text-[hsl(var(--success))]">
                <Lock className="h-2.5 w-2.5" /> Converted
              </Badge>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground font-mono break-all">{r.customer_id}</p>
          {(r.case_setup_required || r.legacy_stage_name) && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid={`kanban-card-legacy-${r.opportunity_id}`}>
              {r.case_setup_required && (
                <Badge variant="outline" className="text-[9px] border-[hsl(var(--warning))]/50 text-[hsl(var(--warning))]">
                  Workflow setup required
                </Badge>
              )}
              {r.legacy_stage_name && (
                <span className="text-[10px] text-muted-foreground" title="Recorded under an obsolete stage name">
                  recorded: {r.legacy_stage_name}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-1.5 flex-wrap">
            <IntentBadge category={r.intent} />
            <span className="text-[10px] text-muted-foreground">{r.owner_name || "Unassigned"}</span>
          </div>
          <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
            <span>Age {ageDays(r.created_at)}d</span>
            <span>{nextFollowUpAt(r) ? `Next: ${fmtIST(nextFollowUpAt(r)).split(",")[0]}` : "No follow-up"}</span>
          </div>
          {/* Read-only journey explanation (same component and endpoint as Customer 360); lazy, so a big board adds no requests. */}
          <WhatHappensNextDisclosure opportunityId={r.opportunity_id} surface="pipeline-card"
            testId={`kanban-card-whn-${r.opportunity_id}`} />
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="pipelines-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Pipelines</h1>
          <p className="text-sm text-muted-foreground">Product pursuits by stage — drag between stages; gates stay enforced.</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap min-w-0">
          <Select value={product} onValueChange={setProduct}>
            <SelectTrigger data-testid="pipeline-product-select" className="h-9 w-56 max-w-full"><SelectValue placeholder="Product" /></SelectTrigger>
            <SelectContent>
              {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-9">
              <TabsTrigger data-testid="pipeline-view-kanban" value="kanban"><LayoutGrid className="h-3.5 w-3.5 mr-1" /> Kanban</TabsTrigger>
              <TabsTrigger data-testid="pipeline-view-table" value="table"><TableProperties className="h-3.5 w-3.5 mr-1" /> Table</TabsTrigger>
            </TabsList>
          </Tabs>
          <IncludeTestToggle includeTest={includeTest} canInclude={canInclude} onChange={setIncludeTest} testId="pipeline-include-test" />
          {hasCap("admin.config") && (
            <Button data-testid="pipeline-builder-button" variant="outline" size="sm" className="h-9" onClick={() => setBuilderOpen(true)}>
              <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Builder
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-2.5 rounded-md border bg-muted/30 px-3 py-2" data-testid="pipeline-include-completed-row">
        <Switch id="pipeline-include-completed" data-testid="pipeline-include-completed"
          checked={includeCompleted} onCheckedChange={(v) => setPlUrlState({ completed: v, cp: 1 })}
          aria-describedby="pipeline-include-completed-help" className="mt-0.5" />
        <div className="min-w-0">
          <Label htmlFor="pipeline-include-completed" className="text-sm font-medium cursor-pointer">Include converted customers</Label>
          <p id="pipeline-include-completed-help" className="text-xs text-muted-foreground" data-testid="pipeline-include-completed-help">
            {includeCompleted
              ? <>Also showing converted customers, newest first. Each sits at its Case&apos;s actual current stage (Closure only once the Case is genuinely finished), is marked <b>Converted</b>, and is read-only here — it can&apos;t be dragged or reopened from the board.</>
              : <>Off (default): only active opportunities are shown — a customer leaves this board once they convert. Turn on to also see converted customers at their Case&apos;s current stage, read-only.</>}
          </p>
          {includeCompleted && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid="pipeline-converted-status">
              <span className="text-xs font-medium" data-testid="pipeline-converted-count">
                {converted.loading && converted.items.length === 0
                  ? "Loading converted customers…"
                  : `Showing ${converted.items.length} of ${converted.total} converted customers`}
              </span>
              {convertedHasMore && (
                <Button data-testid="pipeline-converted-load-more" variant="outline" size="sm" className="h-7 text-xs"
                  disabled={converted.loading} onClick={loadMoreConverted}>
                  {converted.loading ? "Loading…" : `Load more (${Math.min(CONVERTED_PAGE_SIZE, converted.total - converted.items.length)} of ${converted.total - converted.items.length} remaining)`}
                </Button>
              )}
              {converted.error && (
                <span className="text-xs text-destructive" data-testid="pipeline-converted-error">
                  Couldn&apos;t load converted customers — {converted.items.length} of {converted.total} shown.{" "}
                  <button type="button" className="underline" onClick={converted.items.length ? loadMoreConverted : load}>Retry</button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {rows && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="pipeline-active-status">
          <span className="font-medium" data-testid="pipeline-active-count">
            {active.hasMore
              ? `Showing ${formatCount(rows.length)} of ${formatCount(active.total, active.capped)} active opportunities`
              : `${formatCount(rows.length)} active ${rows.length === 1 ? "opportunity" : "opportunities"}`}
          </span>
          {active.hasMore && (
            <Button data-testid="pipeline-active-load-more" variant="outline" size="sm" className="h-7 text-xs"
              disabled={active.loading} onClick={loadMoreActive}>
              {active.loading ? "Loading…" : `Load more (${formatCount(Math.max(0, active.total - rows.length), active.capped)} remaining)`}
            </Button>
          )}
          {active.error && (
            <span className="text-destructive" data-testid="pipeline-active-error">
              {active.error}.{" "}
              <button type="button" className="underline" onClick={active.hasMore ? loadMoreActive : load}>Retry</button>
            </span>
          )}
        </div>
      )}

      {pipelineError || (active.error && !rows) ? (
        <Card>
          <ErrorState testId="pipeline-error"
            title={pipelineError ? "Couldn't load this pipeline's stages" : "Couldn't load opportunities"}
            message={pipelineError ? "The stage list didn't load, so the board can't be drawn." : active.error}
            onRetry={load} />
        </Card>
      ) : !rows || !pipeline ? (
        <div className="flex gap-3 overflow-x-auto" data-testid="pipeline-loading">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-72 w-72 shrink-0" />)}
        </div>
      ) : boardRows.length === 0 ? (
        <Card>
          <EmptyState testId="pipeline-empty" icon={KanbanSquare}
            title={includeCompleted ? "No active or converted opportunities for this product" : "No active opportunities for this product"}
            hint="New leads appear here automatically." />
        </Card>
      ) : view === "kanban" ? (
        <div className="flex gap-3 overflow-x-auto pb-3" data-testid="pipeline-kanban">
          {kanbanColumns.map((col) => {
            const s = col.stage;
            const slug = col.name.replace(/\s+/g, "-");
            const { visible, hidden } = windowed(col.rows, colShown[col.name] ?? COLUMN_WINDOW);
            return (
              <div key={col.key}
                className="w-72 shrink-0"
                onDragOver={s ? (e) => e.preventDefault() : undefined}
                onDrop={s ? () => onDrop(s.name) : undefined}
                data-testid={`kanban-column-${slug}`}>
                <div className={`rounded-t-md border border-b-0 border-t-4 ${(s && STAGE_COLORS[s.color]) || STAGE_COLORS.slate} bg-card px-3 py-2 flex items-center gap-2`}>
                  <span className="text-sm font-semibold min-w-0 break-words">{col.name}</span>
                  <Badge variant="outline" className="text-[10px] ml-auto shrink-0" data-testid={`kanban-column-count-${slug}`}
                    title={active.hasMore ? "More active opportunities exist than are loaded — use Load more" : undefined}>
                    {columnCountLabel(col.rows.length, active.hasMore)}
                  </Badge>
                </div>
                <div className="border rounded-b-md bg-muted/30 p-2 space-y-2 min-h-56">
                  {visible.map(renderKanbanCard)}
                  {hidden > 0 && (
                    <Button data-testid={`kanban-column-more-${slug}`} variant="outline" size="sm" className="w-full h-8 text-xs"
                      onClick={() => setColShown((m) => ({ ...m, [col.name]: (m[col.name] ?? COLUMN_WINDOW) + COLUMN_WINDOW }))}>
                      Show more ({hidden})
                    </Button>
                  )}
                  {col.rows.length === 0 && (
                    <p className="text-[11px] text-muted-foreground text-center pt-6">{s ? "Drop cards here" : "Nothing here"}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card>
          <Table data-testid="pipeline-table">
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead className="hidden md:table-cell">Owner</TableHead>
                <TableHead className="hidden md:table-cell">Intent</TableHead>
                <TableHead className="hidden lg:table-cell">Last contact</TableHead>
                <TableHead className="hidden lg:table-cell">Next follow-up</TableHead>
                <TableHead>Age</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableWindow.visible.map((r) => (
                <Fragment key={r.opportunity_id}>
                <TableRow className="cursor-pointer" data-testid={`pipeline-row-${r.opportunity_id}`}
                  onClick={() => navigate(`/customers/${r.contact_id}?return_to=${returnTo}`)}>
                  <TableCell>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.contact_name}</span>
                      <TestBadge show={r.is_test_record} />
                      {isCompletedRow(r) && (
                        <Badge variant="outline" data-testid={`pipeline-row-completed-${r.opportunity_id}`}
                          className="text-[9px] gap-1 border-[hsl(var(--success))]/40 text-[hsl(var(--success))]">
                          <Lock className="h-2.5 w-2.5" /> Converted
                        </Badge>
                      )}
                    </div>
                    <span className="text-[11px] font-mono text-muted-foreground">{r.customer_id}</span>
                    <div>
                      <button type="button" aria-expanded={journeyRows.has(r.opportunity_id)}
                        className="text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                        data-testid={`pipeline-row-whn-toggle-${r.opportunity_id}`}
                        onClick={(e) => { e.stopPropagation(); toggleJourney(r.opportunity_id); }}>
                        {journeyRows.has(r.opportunity_id) ? "▾" : "▸"} What happens next
                      </button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select value={r.stage_name || ""} disabled={isCompletedRow(r)} onValueChange={(name) => {
                      const stage = pipeline.stages.find((s) => s.name === name);
                      if (stage) moveStage(r, stage);
                    }}>
                      <SelectTrigger className="h-8 w-40 text-xs" onClick={(e) => e.stopPropagation()}
                        title={isCompletedRow(r) ? COMPLETED_READ_ONLY_MSG : undefined}
                        data-testid={`pipeline-stage-select-${r.opportunity_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(pipeline.stages || []).filter((s) => s.active !== false).map((s) => (
                          <SelectItem key={s.stage_id} value={s.name}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{r.owner_name || "—"}</TableCell>
                  <TableCell className="hidden md:table-cell"><IntentBadge category={r.intent} /></TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{r.last_contact_at ? fmtIST(r.last_contact_at) : "Never"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{nextFollowUpAt(r) ? fmtIST(nextFollowUpAt(r)) : "—"}</TableCell>
                  <TableCell className="text-xs">{ageDays(r.created_at)}d</TableCell>
                </TableRow>
                {journeyRows.has(r.opportunity_id) && (
                  <TableRow data-testid={`pipeline-row-whn-${r.opportunity_id}`}>
                    <TableCell colSpan={7} className="bg-muted/20">
                      <WhatHappensNextPanel opportunityId={r.opportunity_id} compact surface="pipeline-table" />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
          <div className="flex flex-wrap items-center gap-2 border-t px-3 py-2 text-xs text-muted-foreground" data-testid="pipeline-table-footer">
            <span data-testid="pipeline-table-count">
              Showing {formatCount(tableWindow.visible.length)} of {formatCount(boardRows.length)} {boardRows.length === 1 ? "row" : "rows"}
            </span>
            {tableWindow.hidden > 0 && (
              <Button data-testid="pipeline-table-more" variant="outline" size="sm" className="h-7 text-xs"
                onClick={() => setTableShown((n) => n + TABLE_WINDOW)}>
                Show more ({formatCount(Math.min(TABLE_WINDOW, tableWindow.hidden))} of {formatCount(tableWindow.hidden)})
              </Button>
            )}
          </div>
        </Card>
      )}

      <StageGateExplanation view={gateExplain} action={gateAction}
        onClose={() => { setGateExplain(null); setGateAction(null); }}
        onOpenCase={(id) => { setGateExplain(null); navigate(`/process/${id}`); }} />
      <OverrideDialog pending={pendingMove} setPending={setPendingMove}
        onConfirm={(reason) => moveStage(pendingMove.opp, pendingMove.stage, true, reason)} />
      <Dialog open={!!setupBlock} onOpenChange={(o) => !o && setSetupBlock(null)}>
        <DialogContent className="sm:max-w-md" data-testid="pipeline-workflow-setup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {setupBlock ? `${setupBlock.opp.contact_name} can't move to "${setupBlock.stage.name}"` : ""}
            </DialogTitle>
          </DialogHeader>
          {setupBlock && (
            <WorkflowSetupRequiredNotice detail={setupBlock.detail} attempted
              testId="pipeline-workflow-setup-required" />
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="pipeline-workflow-setup-close" onClick={() => setSetupBlock(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!backwardPrompt} onOpenChange={(o) => !o && setBackwardPrompt(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Move backward</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This is an authorized correction, not an override — the requirements for the
            stage being left don't need to be complete. A reason is still required and audited.
          </p>
          <Textarea data-testid="backward-move-reason" rows={3} value={backwardReason}
            onChange={(e) => setBackwardReason(e.target.value)}
            placeholder="Why is this opportunity moving backward?" />
          <DialogFooter>
            <Button data-testid="backward-move-confirm" disabled={backwardReason.trim().length < 5}
              onClick={() => moveStage(backwardPrompt.opp, backwardPrompt.stage, false, backwardReason)}>
              Move backward
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {callBlocker && (
        <CallLogDialog
          row={{ contact_id: callBlocker.opp.contact_id, contact_name: callBlocker.opp.contact_name,
                opportunity_id: callBlocker.opp.opportunity_id, product_code: callBlocker.opp.product_code,
                owner_claim_status: "pending_outcome" }}
          open={!!callBlocker} setOpen={(o) => !o && setCallBlocker(null)}
          onLogged={() => moveStage(callBlocker.opp, callBlocker.stage)} />
      )}
      {consultBlocker && (
        <ConsultationActions appt={consultBlocker.appt} forceOpen hideTriggerButtons
          onSaved={(saved) => {
            // 2026-09 R13 — see Customer360Page's identical handler for the
            // full rationale: `saved` is undefined for the dialog's own
            // unrelated intent-save action (ignored); only a genuinely
            // qualifying outcome (isConversion) retries the originally-
            // blocked move, never a Not Ready/Lost/reschedule/cancellation.
            if (!saved) return;
            if (saved.isConversion) { moveStage(consultBlocker.opp, consultBlocker.stage); }
            else { setConsultBlocker(null); load(); }
          }} />
      )}
      {admissionBlocker && (
        <AdmissionOutcomeActions opportunityId={admissionBlocker.opp.opportunity_id} contactName={admissionBlocker.opp.contact_name}
          forceOpen hideTriggerButtons
          onSaved={() => moveStage(admissionBlocker.opp, admissionBlocker.stage)} />
      )}
      {outcomeFor && (
        <WorkflowOutcomeDialog caseId={outcomeFor.caseId} taskId={outcomeFor.taskId}
          contactId={outcomeFor.contactId} open
          onOpenChange={(o) => { if (!o) setOutcomeFor(null); }}
          onSubmitted={() => load()} />
      )}
      <PipelineBuilder open={builderOpen} setOpen={setBuilderOpen} onChanged={load} />
    </div>
  );
}
